// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Column,
  Button,
  InlineNotification,
  Tag,
  ProgressIndicator,
  Modal,
  TextInput,
  Breadcrumb,
  BreadcrumbItem,
  HeaderPanel,
  SideNav,
  SideNavItems,
  SideNavLink,
} from '@carbon/react';
import { UserMultiple, Chat, Screen, DocumentAdd, CheckmarkFilled, InProgress, Warning } from '@carbon/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useCollaboration } from '../../hooks/useCollaboration';
import CollaborationSession from '../../components/collaboration/CollaborationSession';
import ParticipantsList from '../../components/collaboration/ParticipantsList';
import CollaborativeEditor from '../../components/collaboration/CollaborativeEditor';
import CursorTracker from '../../components/collaboration/CursorTracker';
import AnnotationLayer from '../../components/collaboration/AnnotationLayer';

const ClassroomCollaborate: React.FC = () => {
  const { sessionId, classId } = useParams<{ sessionId?: string; classId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [newSessionModalOpen, setNewSessionModalOpen] = useState(!sessionId && !classId);
  const [sessionName, setSessionName] = useState('');
  const [sessionType, setSessionType] = useState<'quiz' | 'assignment' | 'discussion'>('discussion');
  const [createdSessionId, setCreatedSessionId] = useState<string | null>(null);

  const {
    participants,
    isConnected,
    isReconnecting,
    connectionError,
    cursors,
    selections,
    annotations,
    edits,
    conflicts,
    capabilities,
    sendEdit,
    sendCursorPosition,
    shareSelection,
    createAnnotation,
    resolveConflict,
  } = useCollaboration({
    sessionId: sessionId || classId || createdSessionId || undefined,
    autoConnect: true,
    onConnect: () => console.log('Connected to classroom collaboration'),
    onDisconnect: () => console.log('Disconnected from classroom collaboration'),
    onError: error => console.error('Collaboration error:', error),
  });

  const createNewSession = async () => {
    if (!sessionName) return;

    try {
      const response = await fetch('/api/v1/collaboration/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          name: sessionName,
          type: 'classroom',
          metadata: {
            sessionType,
            subject: user?.role === 'teacher' ? user.subject : undefined,
            grade: user?.role === 'teacher' ? user.grade : undefined,
          },
        }),
      });

      if (response.ok) {
        const { sessionId: newId } = await response.json();
        setCreatedSessionId(newId);
        setNewSessionModalOpen(false);
        navigate(`/classroom/collaborate/${newId}`);
      } else {
        console.error('Failed to create session');
      }
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const getStatusIcon = () => {
    if (isConnected) return <CheckmarkFilled size={16} />;
    if (isReconnecting) return <InProgress size={16} />;
    if (connectionError) return <Warning size={16} />;
    return null;
  };

  const getStatusColor = () => {
    if (isConnected) return 'green';
    if (isReconnecting) return 'gray';
    if (connectionError) return 'red';
    return 'gray';
  };

  return (
    <div className="classroom-collaborate">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <Breadcrumb>
            <BreadcrumbItem href="/">Home</BreadcrumbItem>
            <BreadcrumbItem href="/classroom">Classroom</BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>Collaborate</BreadcrumbItem>
          </Breadcrumb>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <div className="page-header">
            <h1>Classroom Collaboration</h1>
            <div className="status-indicators" role="status" aria-live="polite" aria-atomic="true">
              <Tag
                type={getStatusColor()}
                aria-label={`Connection status: ${isConnected ? 'Connected' : isReconnecting ? 'Reconnecting' : 'Disconnected'}`}
              >
                {getStatusIcon()}
                {isConnected ? 'Connected' : isReconnecting ? 'Reconnecting...' : 'Disconnected'}
              </Tag>
              <Tag type="blue" aria-label={`${participants.length} participants in session`}>
                <UserMultiple size={16} />
                {participants.length} Participants
              </Tag>
            </div>
          </div>
        </Column>

        {connectionError && (
          <Column lg={16} md={8} sm={4}>
            <div role="alert" aria-live="assertive">
              <InlineNotification
                kind="error"
                title="Connection Error"
                subtitle={connectionError.message}
                lowContrast
                aria-describedby="connection-error-desc"
              />
              <span id="connection-error-desc" className="sr-only">
                Connection error occurred: {connectionError.message}
              </span>
            </div>
          </Column>
        )}

        <Column lg={12} md={6} sm={4}>
          <div className="main-content">
            {sessionId || classId || createdSessionId ? (
              <CollaborationSession
                sessionId={sessionId || classId || createdSessionId || ''}
                userId={user?.user_id || ''}
                token={localStorage.getItem('auth_token') || ''}
                mode={user?.role === 'teacher' ? 'moderator' : 'participant'}
                sessionTitle={classId ? `Live Class: ${classId}` : 'Classroom Collaboration'}
              />
            ) : (
              <div className="no-session">
                <p>No active collaboration session</p>
                <Button onClick={() => setNewSessionModalOpen(true)}>Start New Session</Button>
              </div>
            )}
          </div>
        </Column>

        <Column lg={4} md={2} sm={4}>
          <div className="sidebar">
            <ParticipantsList participants={participants} currentUserId={user?.user_id} />

            <div className="collaboration-tools">
              <h4>Tools</h4>
              <div className="tool-buttons">
                {capabilities.canVoiceChat && (
                  <Button kind="tertiary" size="sm" renderIcon={Chat}>
                    Voice Chat
                  </Button>
                )}
                {capabilities.canScreenShare && (
                  <Button kind="tertiary" size="sm" renderIcon={Screen}>
                    Share Screen
                  </Button>
                )}
                {capabilities.canEdit && (
                  <Button kind="tertiary" size="sm" renderIcon={DocumentAdd}>
                    Co-edit Mode
                  </Button>
                )}
              </div>
            </div>

            {user?.role === 'teacher' && (
              <div className="moderation-tools">
                <h4>Moderation</h4>
                <Button kind="ghost" size="sm">
                  Lock Document
                </Button>
                <Button kind="ghost" size="sm">
                  Clear Annotations
                </Button>
                <Button kind="ghost" size="sm">
                  End Session
                </Button>
              </div>
            )}
          </div>
        </Column>
      </Grid>

      <Modal
        open={newSessionModalOpen}
        onRequestClose={() => setNewSessionModalOpen(false)}
        modalHeading="Create New Collaboration Session"
        primaryButtonText="Create"
        secondaryButtonText="Cancel"
        onRequestSubmit={createNewSession}
      >
        <TextInput
          id="session-name"
          labelText="Session Name"
          placeholder="Enter session name"
          value={sessionName}
          onChange={e => setSessionName(e.target.value)}
        />
        <div className="session-type-selector">
          <label>Session Type:</label>
          <Tag type={sessionType === 'quiz' ? 'blue' : 'gray'} onClick={() => setSessionType('quiz')}>
            Quiz
          </Tag>
          <Tag type={sessionType === 'assignment' ? 'blue' : 'gray'} onClick={() => setSessionType('assignment')}>
            Assignment
          </Tag>
          <Tag type={sessionType === 'discussion' ? 'blue' : 'gray'} onClick={() => setSessionType('discussion')}>
            Discussion
          </Tag>
        </div>
      </Modal>
    </div>
  );
};

export default ClassroomCollaborate;
